const STATUS_CODES = require("./photographyStatus.cjs");
const { APPOINTMENT, PAYMENT, REFUND, numericStatus } = STATUS_CODES;
const asText = (value) => value === null || value === undefined ? "" : String(value);
const asMoney = (value) => {
    const amount = Number(value);
    return Number.isFinite(amount) && amount >= 0 ? amount.toFixed(2) : null;
};

const normalizePaymentAttempt = (item = {}) => ({
    paymentRequestNo: asText(item.paymentRequestNo),
    paymentStatus: numericStatus(item.paymentStatus, PAYMENT),
    channel: asText(item.channel),
    invocationPayload: asText(item.invocationPayload),
    amount: asMoney(item.amount),
    refundNo: asText(item.refundNo),
    refundAmount: asMoney(item.refundAmount),
    refundStatus: numericStatus(item.refundStatus, REFUND),
    expiresAt: asText(item.expiresAt),
    paidAt: asText(item.paidAt),
    refundCompletedAt: asText(item.refundCompletedAt),
    createdAt: asText(item.createdAt),
});

const normalizePaymentCreator = (item) => item ? ({
    professionalIdentityCode: asText(item.professionalIdentityCode),
    realNameMasked: asText(item.realNameMasked),
    portrait: {
        accessUrl: asText(item.portrait && item.portrait.accessUrl),
        expiresAt: asText(item.portrait && item.portrait.expiresAt),
    },
}) : null;

const normalizePhotographyPaymentAppointment = (item = {}) => ({
    appointmentNo: asText(item.appointmentNo),
    appointmentStatus: numericStatus(item.appointmentStatus, APPOINTMENT),
    paymentStatus: numericStatus(item.paymentStatus, PAYMENT),
    refundStatus: numericStatus(item.refundStatus, REFUND),
    paymentDeadline: asText(item.paymentDeadline),
    paymentRemainingSeconds: Math.max(0, Number(item.paymentRemainingSeconds) || 0),
    contactPhone: asText(item.contactPhone),
    serviceItemId: asText(item.serviceItemId),
    venueId: asText(item.venueId),
    pointId: asText(item.pointId),
    photographerIdentityCode: asText(item.photographerIdentityCode),
    makeupArtistIdentityCode: asText(item.makeupArtistIdentityCode),
    photographer: normalizePaymentCreator(item.photographer || item.photographerProfile),
    makeupArtist: normalizePaymentCreator(item.makeupArtist || item.makeupArtistProfile),
    themeName: asText(item.themeName),
    styleName: asText(item.styleName),
    packageName: asText(item.packageName),
    provinceName: asText(item.provinceName),
    cityName: asText(item.cityName),
    districtName: asText(item.districtName),
    venueName: asText(item.venueName),
    pointName: asText(item.pointName),
    venueLocationSummary: asText(item.venueLocationSummary),
    peopleCount: Number(item.peopleCount) || 0,
    makeupSelected: item.makeupSelected === true,
    totalAmount: asMoney(item.totalAmount),
    couponDiscountAmount: asMoney(item.couponDiscountAmount),
    payableAmount: asMoney(item.payableAmount),
    shootAt: asText(item.shootAt),
    currentPaymentRequest: item.currentPaymentRequest ? normalizePaymentAttempt(item.currentPaymentRequest) : null,
});

const paymentState = (key, title, description, terminal, canPay, shouldPoll) => ({
    key,
    title,
    description,
    terminal,
    canPay,
    shouldPoll,
});

const defaultSwitchKeyFactory = () => `payment-switch-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
const createPaymentSwitchPayload = (appointmentNo, keyFactory = defaultSwitchKeyFactory, paymentChannel = "ALIPAY_PAGE") => ({
    idempotencyKey: asText(keyFactory(appointmentNo)).slice(0, 100),
    channel: asText(paymentChannel),
});

const isAlipayWebsitePaymentChannel = (channel) => ["ALIPAY_PAGE", "ALIPAY_WAP"].includes(asText(channel));

const WEB_PAYMENT_CHANNELS = {
    WECHAT_NATIVE: { channel: "WECHAT_NATIVE", label: "微信支付", description: "微信扫码支付" },
    ALIPAY_PAGE: { channel: "ALIPAY_PAGE", label: "支付宝", description: "电脑网站支付" },
    ALIPAY_WAP: { channel: "ALIPAY_WAP", label: "支付宝", description: "手机网站支付" },
};
const normalizeAvailablePaymentChannels = (payload = {}, browserChannel = "ALIPAY_PAGE") => {
    const allowedChannels = browserChannel === "ALIPAY_WAP"
        ? ["ALIPAY_WAP"]
        : ["WECHAT_NATIVE", "ALIPAY_PAGE"];
    return Array.isArray(payload.availableChannels)
        ? payload.availableChannels
            .filter(channel => allowedChannels.includes(asText(channel)))
            .map(channel => WEB_PAYMENT_CHANNELS[asText(channel)])
        : [];
};
const parseWechatNativeCodeUrl = (attempt = {}) => {
    if (asText(attempt.channel) !== "WECHAT_NATIVE") return "";
    try {
        const payload = JSON.parse(asText(attempt.invocationPayload));
        const codeUrl = asText(payload && payload.codeUrl).trim();
        return codeUrl.startsWith("weixin://") ? codeUrl : "";
    } catch (error) {
        return "";
    }
};

const getPhotographyPaymentMethods = (appointment = {}) => {
    const current = appointment.currentPaymentRequest;
    if (!current || !WEB_PAYMENT_CHANNELS[current.channel]) return [];
    return [{
        channel: current.channel,
        label: WEB_PAYMENT_CHANNELS[current.channel].label,
        description: WEB_PAYMENT_CHANNELS[current.channel].description,
        selected: true,
    }];
};

const DESKTOP_SWITCH_CHANNELS = ["WECHAT_NATIVE", "ALIPAY_PAGE"];
const isDesktopPhotographyPaymentChannel = (channel) => DESKTOP_SWITCH_CHANNELS.includes(asText(channel));
const getPhotographyPaymentSwitchTarget = (appointment = {}, availableChannels = []) => {
    const current = appointment.currentPaymentRequest;
    if (appointment.appointmentStatus !== APPOINTMENT.WAITING_PAYMENT
        || !current || current.paymentStatus !== PAYMENT.PENDING || appointment.paymentRemainingSeconds <= 0
        || !isDesktopPhotographyPaymentChannel(current.channel)) return null;
    return availableChannels.find((item) => item
        && isDesktopPhotographyPaymentChannel(item.channel)
        && item.channel !== current.channel) || null;
};

const resolvePhotographyPaymentState = (appointment = {}) => {
    if (appointment.refundStatus === REFUND.PROCESSING) {
        return paymentState("refunding", "退款处理中", "服务端已发起原路退款，请刷新查看最新状态。", false, false, true);
    }
    if (appointment.appointmentStatus === APPOINTMENT.PAID) {
        return paymentState("paid", "支付成功", "支付已到账，平台将开始为你匹配服务人员。", true, false, false);
    }
    if (appointment.appointmentStatus === APPOINTMENT.CLOSED) {
        return paymentState("closed", "预约已关闭", "本次预约已由服务端关闭。", true, false, false);
    }
    if (appointment.appointmentStatus !== APPOINTMENT.WAITING_PAYMENT) {
        return paymentState("loading", "正在确认支付状态", "请稍候，页面正在读取服务端最终结果。", false, false, false);
    }
    if (appointment.paymentRemainingSeconds === 0 && appointment.appointmentStatus === APPOINTMENT.WAITING_PAYMENT) {
        return paymentState("deadline_reached", "付款期限已到，正在确认最终结果", "请等待服务端确认支付、关闭或过期事实，也可手动刷新最新状态。", false, false, true);
    }
    const currentRequest = appointment.currentPaymentRequest;
    if (!currentRequest) return paymentState("ready", "等待付款", "请核对优惠与金额，选择可用支付方式后点击支付。", false, true, false);
    if (currentRequest && currentRequest.paymentStatus === PAYMENT.CREATING) {
        return paymentState("creating", "正在创建支付请求", "支付请求正在创建，请稍后手动刷新结果。", false, false, true);
    }
    if (currentRequest && currentRequest.paymentStatus === PAYMENT.CREATE_UNKNOWN) {
        return paymentState("create_unknown", "支付入口确认中", "支付入口正在由服务端确认，请勿重复创建预约或支付请求。请稍后手动刷新。", false, false, true);
    }
    if (currentRequest && currentRequest.paymentStatus === PAYMENT.PENDING && appointment.paymentRemainingSeconds > 0) {
        const nativeCodeUrl = parseWechatNativeCodeUrl(currentRequest);
        const description = currentRequest.channel === "WECHAT_NATIVE"
            ? "请使用微信扫描二维码，并在付款截止时间前完成支付。"
            : "请在付款截止时间前完成支付宝支付。";
        const canPay = isAlipayWebsitePaymentChannel(currentRequest.channel) && !!currentRequest.invocationPayload;
        return paymentState("pending", "等待支付", description, false, canPay, !!nativeCodeUrl || canPay);
    }
    if (appointment.appointmentStatus === APPOINTMENT.WAITING_PAYMENT) {
        return paymentState("request_unavailable", "支付请求暂不可用", "当前没有可使用的支付请求，请刷新服务端状态后重试。", false, false, true);
    }
    return paymentState("loading", "正在确认支付状态", "请稍候，页面正在读取服务端最终结果。", false, false, false);
};

const MOBILE_BROWSER_PATTERN = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
const resolvePhotographyPaymentChannel = (environment = {}) => {
    const clientType = asText(environment.clientType).trim().toUpperCase();
    if (["WEAPP", "MINIAPP", "WECHAT_MINIAPP"].includes(clientType)) return "WECHAT_MINIAPP";
    if (environment.mobile === true || MOBILE_BROWSER_PATTERN.test(asText(environment.userAgent))) return "ALIPAY_WAP";
    return "ALIPAY_PAGE";
};
const getBrowserPhotographyPaymentChannel = (navigatorLike = {}) => resolvePhotographyPaymentChannel({
    clientType: "WEB",
    mobile: navigatorLike.userAgentData && navigatorLike.userAgentData.mobile,
    userAgent: navigatorLike.userAgent,
});

module.exports = {
    normalizePhotographyPaymentRequest: normalizePaymentAttempt,
    STATUS_CODES,
    createPaymentSwitchPayload,
    getBrowserPhotographyPaymentChannel,
    getPhotographyPaymentMethods,
    getPhotographyPaymentSwitchTarget,
    isAlipayWebsitePaymentChannel,
    isDesktopPhotographyPaymentChannel,
    normalizeAvailablePaymentChannels,
    normalizePhotographyPaymentAppointment,
    parseWechatNativeCodeUrl,
    resolvePhotographyPaymentChannel,
    resolvePhotographyPaymentState,
};
